import { useCallback, useMemo, useState } from 'react';
import type { FieldSchema } from '../types';
import type { Question } from '../models/Question';
import type { Group } from '../models/Group';
import { useFormContext } from '../context/FormContext';

export interface UseFormFieldResult {
  question: Question;
  answer: unknown;
  setAnswer: (value: unknown) => void;
  show: boolean;
  showValidationMsg: boolean;
  validationMessage: string;
  alertMsg: string;
  helpTextOpen: boolean;
  toggleQuestionHelpText: () => void;
  questionHelpTextId: string;
  helpTextExtraClass: string;
  getQuestionElementId: () => string;
  hasAnswered: () => boolean;
}

export function useFormField(
  fieldSchema: FieldSchema,
  group: Group
): UseFormFieldResult {
  const {
    formService,
    displayService,
    validationService,
    fieldInfoService,
    stateService,
    forceUpdate,
  } = useFormContext();

  const question = useMemo<Question>(() => {
    return formService.getQuestionById(fieldSchema.id, group)!;
  }, [formService, fieldSchema.id, group]);

  const [helpTextOpen, setHelpTextOpen] = useState<boolean>(false);

  const questionHelpTextId = `help-text-q-${fieldSchema.id}${group.index}`;
  const helpTextExtraClass = fieldSchema.openHelpText ? 'start-open' : '';

  const setAnswer = useCallback<(value: unknown) => void>(
    (value: unknown) => {
      question.answer = value;
      forceUpdate();
    },
    [question, forceUpdate]
  );

  const show = displayService.showQuestion(question, group);

  const validationResponse = validationService.validate(question, group);
  const showValidationMsg =
    stateService.showErrors && !validationResponse.valid && show;

  const toggleQuestionHelpText = useCallback<() => void>(() => {
    setHelpTextOpen((open) => !open);
  }, []);

  const getQuestionElementId = useCallback<() => string>(() => {
    return `${group.id}-${group.index}-${question.id}`;
  }, [group, question.id]);

  return {
    question,
    answer: question.answer,
    setAnswer,
    show,
    showValidationMsg,
    validationMessage: validationResponse.message || '',
    alertMsg: fieldInfoService.getAlert(question, group),
    helpTextOpen,
    toggleQuestionHelpText,
    questionHelpTextId,
    helpTextExtraClass,
    getQuestionElementId,
    hasAnswered: () => question.hasAnswered(),
  };
}
